import React, { useEffect, useState } from 'react'
// eslint-disable-next-line import/no-unresolved
import RecoveryKeyPanel from '@devnw/cyber-iam/auth/RecoveryKeyPanel/RecoveryKeyPanel'
import type { SetupClient } from '../../../api/setupClient'
import { friendlySetupError } from '../setupErrors'
import {
  StepContainerWide,
  StepHeading,
  StepDescription,
  ErrorAlert,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
} from '../setupStyled'

interface RecoveryKeyProps {
  client: SetupClient
  onNext: () => void
  onBack: () => void
}

/**
 * Fourth step: generate the account-recovery key.
 *
 * SECURITY INVARIANT (binding): the recovery key is shown ONCE. This
 * component holds it only in React state for the lifetime of this step —
 * it is NEVER written to localStorage/sessionStorage/a cookie, and there is
 * no "show it again" affordance; navigating away loses it forever, exactly
 * like the server (which stores only a verifier, never the plaintext — see
 * recovery_key_server.go). Next is gated on an explicit "I have saved this
 * key" checkbox so the operator cannot advance past this step by accident.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI Card/Checkbox onto
 * `@devnw/cyber-iam/auth/RecoveryKeyPanel`, which owns the key display +
 * copy-to-clipboard ceremony and the confirm-checkbox rendering; this step
 * still owns the recoveryGenerate() fetch, the confirmed state (so the
 * page-level Next button can gate on it, matching the pre-migration
 * layout), and the friendly-error mapping.
 */
export const RecoveryKey = ({
  client,
  onNext,
  onBack,
}: RecoveryKeyProps): JSX.Element => {
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null)
  const [status, setStatus] = useState<'working' | 'done' | 'error'>('working')
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    let cancelled = false
    client
      .recoveryGenerate()
      .then((resp) => {
        if (cancelled) return
        setRecoveryKey(resp.recovery_key)
        setStatus('done')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(friendlySetupError(e, 'Failed to generate the recovery key'))
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [client]) // mount-only (client is a stable ref): generate the recovery key exactly once per wizard-step mount

  return (
    <StepContainerWide>
      <StepHeading>Recovery key</StepHeading>
      <StepDescription>
        This key lets you recover access to the admin account if you lose every
        enrolled MFA factor. It is shown only once and is not stored anywhere by
        this browser — copy it to a password manager or other secure location
        now.
      </StepDescription>

      {status === 'working' && (
        <StepDescription aria-busy="true">
          Generating recovery key…
        </StepDescription>
      )}

      {status === 'error' && error && <ErrorAlert role="alert">{error}</ErrorAlert>}

      {status === 'done' && recoveryKey && (
        <RecoveryKeyPanel
          testID="setup-recovery-key"
          recoveryKey={recoveryKey}
          confirmed={confirmed}
          onConfirmChange={setConfirmed}
        />
      )}

      <ButtonRow>
        <SecondaryButton type="button" onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onNext} disabled={!confirmed}>
          Next
        </PrimaryButton>
      </ButtonRow>
    </StepContainerWide>
  )
}
